import { useState, type ReactNode } from 'react';
import type { BlogDraft } from '@/data/copilot';
import { ConnectorGatedNotice } from './ConnectorGatedNotice.tsx';
import { WordPressPushControl } from './WordPressPushControl.tsx';
import { ContentTypeSelector } from './ContentTypeSelector.tsx';
import {
  DEFAULT_CONTENT_TYPE,
  blogDraftCopyHtml,
  blogDraftCopyText,
  blogSeoEvidenceLines,
  parseMarkdownBlocks,
  type ContentTypeId,
} from './contentActionModel.ts';
import { updateBlogDraft } from '@/data/sources-content';
import { ApiError } from '@/lib/api';
import { repurposeBlogToSocial, type RepurposeResult } from '@/data/sources-repurpose';
import {
  BLOG_EDIT_LIMITS,
  blogEditChanges,
  blogEditInitial,
  firstBlogEditLengthError,
  type BlogEditField,
  type BlogEditForm,
} from '@/data/blog-edit-model';

/* ----------------------------------------------------------------------------
 * BlogDraftView (P1) — the POPULATED long-form blog_article draft rendered under
 * AI Studio on the accept 200 (next.flow==='blog_draft'). It shows the SEO
 * surface (title / slug / meta_title / meta_description), the article body, the
 * CTA and the SEO evidence, plus a content-type selector and three controls:
 * Copy text (plain/markdown body), Copy HTML (body_html), Repurpose for social.
 *
 * SAFETY: the article body is rendered from body_md as React elements via the
 * pure block parser (NO dangerouslySetInnerHTML, NO external sanitizer). The
 * raw body_html is used ONLY for the Copy-HTML clipboard action — it is never
 * injected into the DOM. Repurpose is a CLIENT STUB: it hands the draft to the
 * existing social path (an intent + optional callback); it performs NO social
 * generation and NO publish/schedule/send (connectors stay gated).
 * -------------------------------------------------------------------------- */

const primaryBtn =
  'inline-flex items-center justify-center rounded-full border border-ai bg-ai-wash/60 px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors duration-fast hover:bg-ai-wash max-lg:min-h-[44px]';
const outlineBtn =
  'inline-flex items-center justify-center rounded-sm border border-border-default px-2.5 py-1.5 text-[11px] text-text-secondary transition-colors duration-fast hover:bg-hover hover:text-text-primary max-lg:min-h-[44px]';
const ghostBtn =
  'inline-flex items-center justify-center rounded-sm px-2 py-1.5 text-[11px] text-text-tertiary transition-colors duration-fast hover:bg-hover hover:text-text-primary max-lg:min-h-[44px]';

/** Only http(s) or root-relative hrefs are allowed; anything else renders as
 *  plain text (defends against javascript: and data: URLs in generated links). */
function safeHref(url: string): string | null {
  return /^(https?:\/\/|\/)/i.test(url) ? url : null;
}

/**
 * Render inline markdown emphasis to React nodes: [text](url), **bold**,
 * *italic*, and `code`. No HTML injection — everything is a React element or a
 * plain string. Unknown/unsafe links degrade to their visible text.
 */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const re = /(\[([^\]]+)\]\(([^)\s]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*|_([^_]+)_)|(\`([^\`]+)\`)/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1]) {
      const href = safeHref(m[3]);
      nodes.push(
        href ? (
          <a
            key={k++}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="text-ai underline decoration-ai/40 underline-offset-2 hover:decoration-ai"
          >
            {m[2]}
          </a>
        ) : (
          m[2]
        ),
      );
    } else if (m[4]) {
      nodes.push(<strong key={k++} className="font-semibold text-text-primary">{m[5]}</strong>);
    } else if (m[6]) {
      nodes.push(<em key={k++}>{m[7] ?? m[8]}</em>);
    } else if (m[9]) {
      nodes.push(
        <code
          key={k++}
          className="rounded-sm bg-inset px-1 py-0.5 font-mono text-[0.85em] text-text-primary"
        >
          {m[10]}
        </code>,
      );
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** The article body, rendered from body_md as SAFE React elements. */
function MarkdownArticle({ md }: { md: string }) {
  const blocks = parseMarkdownBlocks(md);
  if (blocks.length === 0) {
    return (
      <p className="text-[13px] italic text-text-tertiary">The draft body is empty.</p>
    );
  }
  return (
    <div className="space-y-2.5 text-[13px] leading-relaxed text-text-primary">
      {blocks.map((b, i) => {
        switch (b.type) {
          case 'heading': {
            const cls =
              b.level === 1
                ? 'text-base font-semibold text-text-primary'
                : b.level === 2
                  ? 'text-sm font-semibold text-text-primary'
                  : 'text-[13px] font-semibold text-text-secondary';
            if (b.level === 1) return <h3 key={i} className={cls}>{renderInline(b.text)}</h3>;
            if (b.level === 2) return <h4 key={i} className={cls}>{renderInline(b.text)}</h4>;
            return <h5 key={i} className={cls}>{renderInline(b.text)}</h5>;
          }
          case 'ul':
            return (
              <ul key={i} className="list-disc space-y-1 pl-5">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ul>
            );
          case 'ol':
            return (
              <ol key={i} className="list-decimal space-y-1 pl-5">
                {b.items.map((it, j) => (
                  <li key={j}>{renderInline(it)}</li>
                ))}
              </ol>
            );
          case 'blockquote':
            return (
              <blockquote
                key={i}
                className="border-l-2 border-ai/40 pl-3 text-text-secondary"
              >
                {renderInline(b.text)}
              </blockquote>
            );
          case 'hr':
            return <hr key={i} className="border-border-subtle" />;
          case 'paragraph':
          default:
            return (
              <p key={i} className="whitespace-pre-line">
                {renderInline(b.text)}
              </p>
            );
        }
      })}
    </div>
  );
}

/** A labelled SEO field row (meta title / description) with a soft length hint. */
function SeoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[92px_1fr] gap-2 text-xs">
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="text-text-primary">
        {value.trim() !== '' ? value : <span className="text-text-tertiary">&mdash;</span>}
      </dd>
    </div>
  );
}

/** Map an edit-save ApiError (422 validation_failed / invalid_field, or an auth
 *  failure) into an honest, human message. The server is the SEO/Yoast
 *  authority; a rejected edit persisted nothing, so the prior ready draft stands. */
function editSaveMessage(e: ApiError): string {
  if (e.status === 401 || e.status === 403) {
    return 'Your session has expired or you are not permitted to edit this draft. Refresh and try again.';
  }
  if (e.status === 422) {
    const details = (e.details ?? {}) as Record<string, unknown>;
    const reason = typeof details.reason === 'string' ? details.reason : e.code;
    const friendly: Record<string, string> = {
      empty_body: 'The article body cannot be empty.',
      missing_title: 'The title cannot be empty.',
      missing_slug: 'The slug cannot be empty.',
      keyphrase_not_in_intro:
        'The focus keyphrase must still appear in the opening paragraph — keep it in your intro.',
      missing_h2: 'Keep at least one “##” section heading in the body.',
      unresolved_placeholder: 'Remove the placeholder text (e.g. “[your town]”) before saving.',
      seo_keyword_not_in_first_para: 'Keep the SEO keyword in the opening paragraph.',
      seo_keyword_not_in_final_para: 'Keep the SEO keyword in the closing paragraph.',
      seo_target_link_missing_first_para: 'Keep the SEO target link in the opening paragraph.',
      seo_target_link_missing_final_para: 'Keep the SEO target link in the closing paragraph.',
    };
    if (e.code === 'invalid_field') {
      const field = typeof details.field === 'string' ? details.field.replace(/_/g, ' ') : 'A field';
      return `${field} is too long. Shorten it and try again.`;
    }
    return friendly[reason] ?? 'That change would break a required SEO/Yoast check. Nothing was saved.';
  }
  return 'Could not save your changes. Nothing was changed. Try again.';
}

const editInputCls =
  'w-full rounded-md border border-border-default bg-canvas px-3 py-2 text-[13px] text-text-primary outline-none placeholder:text-text-tertiary focus:border-ai';
const editLabelCls = 'block text-[10px] font-semibold uppercase tracking-wide text-text-tertiary';

/** The inline editor for the four human-authored fields. Server re-validates on
 *  Save; nothing here bypasses a control. */
function DraftEditForm({
  form,
  onChange,
  onSave,
  onCancel,
  saving,
  error,
}: {
  form: BlogEditForm;
  onChange: (field: BlogEditField, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving: boolean;
  error: string | null;
}) {
  return (
    <div className="mt-3 rounded-md border border-ai/40 bg-canvas p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-ai">
        Edit draft before pushing
      </div>
      <p className="mb-3 text-[11px] leading-snug text-text-tertiary">
        Make final changes to the title, slug, meta title and body. Aura re-checks SEO/Yoast on
        save — the featured image, focus keyword and links are kept.
      </p>

      <div className="space-y-3">
        <div>
          <label className={editLabelCls} htmlFor="edit-title">Content title</label>
          <input
            id="edit-title"
            className={`${editInputCls} mt-1`}
            value={form.title}
            maxLength={BLOG_EDIT_LIMITS.title}
            onChange={(e) => onChange('title', e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label className={editLabelCls} htmlFor="edit-slug">Slug</label>
          <input
            id="edit-slug"
            className={`${editInputCls} mt-1 font-mono text-[12px]`}
            value={form.slug}
            maxLength={BLOG_EDIT_LIMITS.slug}
            onChange={(e) => onChange('slug', e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label className={editLabelCls} htmlFor="edit-meta-title">Meta title</label>
          <input
            id="edit-meta-title"
            className={`${editInputCls} mt-1`}
            value={form.meta_title}
            maxLength={BLOG_EDIT_LIMITS.meta_title}
            onChange={(e) => onChange('meta_title', e.target.value)}
            autoComplete="off"
          />
        </div>
        <div>
          <label className={editLabelCls} htmlFor="edit-body">Article body (markdown)</label>
          <textarea
            id="edit-body"
            className={`${editInputCls} mt-1 min-h-[220px] font-mono text-[12px] leading-relaxed`}
            value={form.body_md}
            maxLength={BLOG_EDIT_LIMITS.body_md}
            onChange={(e) => onChange('body_md', e.target.value)}
          />
        </div>
      </div>

      {error && (
        <p role="alert" className="mt-2 text-[12px] text-danger">
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center gap-2">
        <button type="button" onClick={onSave} disabled={saving} className={`${primaryBtn} disabled:opacity-60`}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button type="button" onClick={onCancel} disabled={saving} className={ghostBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export function BlogDraftView({
  draft,
  onDismiss,
  onRepurpose,
  onSaved,
  projectId,
}: {
  draft: BlogDraft;
  onDismiss: () => void;
  /** The active "Viewing" domain — tags the repurposed social drafts to the
   *  source blog's domain so they surface in the domain-scoped Social library. */
  projectId?: number | null;
  /** Optional handoff: the orchestrator wires the real social path. When absent
   *  the control still records the intent honestly (no generation happens). */
  onRepurpose?: (draft: BlogDraft) => void;
  /** Called with the SAVED draft after a successful human edit, so the parent can
   *  re-render (and refresh any list/cache) with the edited values. */
  onSaved?: (draft: BlogDraft) => void;
}) {
  // A saved edit overrides the incoming prop for display; until then the prop
  // flows through (so a parent re-open of a different draft still works).
  const [saved, setSaved] = useState<BlogDraft | null>(null);
  const view = saved ?? draft;
  const canEdit = view.status === 'ready';

  const [selectedType, setSelectedType] = useState<ContentTypeId>(
    (view.content_type as ContentTypeId) || DEFAULT_CONTENT_TYPE,
  );
  const [copied, setCopied] = useState<'text' | 'html' | null>(null);
  // Repurpose-for-social: calls the real endpoint, then surfaces the created
  // draft social posts inline for review. Draft-only — nothing is published/sent.
  const [repurposeState, setRepurposeState] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [repurposeResult, setRepurposeResult] = useState<RepurposeResult | null>(null);
  const [repurposeError, setRepurposeError] = useState<string | null>(null);

  // Edit state.
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BlogEditForm>(() => blogEditInitial(view));
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const startEdit = () => {
    setForm(blogEditInitial(view));
    setSaveError(null);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };
  const changeField = (field: BlogEditField, value: string) =>
    setForm((f) => ({ ...f, [field]: value }));

  const save = async () => {
    const changes = blogEditChanges(blogEditInitial(view), form);
    if (Object.keys(changes).length === 0) {
      setEditing(false);
      return;
    }
    const lenErr = firstBlogEditLengthError(changes);
    if (lenErr) {
      setSaveError(`${lenErr.field.replace(/_/g, ' ')} must be ${lenErr.limit} characters or fewer.`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateBlogDraft(view.id, changes);
      setSaved(updated);
      setEditing(false);
      onSaved?.(updated);
    } catch (e) {
      setSaveError(e instanceof ApiError ? editSaveMessage(e) : 'Could not save your changes. Nothing was changed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const copy = async (kind: 'text' | 'html') => {
    const payload = kind === 'text' ? blogDraftCopyText(view) : blogDraftCopyHtml(view);
    try {
      await navigator.clipboard.writeText(payload);
      setCopied(kind);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard unavailable — non-fatal */
    }
  };

  const repurpose = async () => {
    if (repurposeState === 'loading') return;
    setRepurposeState('loading');
    setRepurposeError(null);
    try {
      const result = await repurposeBlogToSocial({
        blogDraftId: view.id,
        projectId,
        title: view.title,
        body_md: view.body_md,
        seo_evidence: view.seo_evidence,
      });
      setRepurposeResult(result);
      setRepurposeState('done');
      // Notify the parent (e.g. to refresh the social content list) ONLY when
      // real drafts were created. The button works with or without this prop.
      if (result.available && result.valid && result.drafts.length > 0) {
        onRepurpose?.(view);
      }
    } catch (e) {
      setRepurposeError(
        e instanceof ApiError
          ? 'Could not prepare social drafts. Nothing was created. Try again.'
          : 'Could not prepare social drafts. Nothing was created. Try again.',
      );
      setRepurposeState('error');
    }
  };

  const seo = blogSeoEvidenceLines(view.seo_evidence);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-ai">
          &#10023; blog draft ready
        </span>
        <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
          Draft
        </span>
      </div>

      <ContentTypeSelector selected={selectedType} onSelect={setSelectedType} />

      {editing ? (
        <DraftEditForm
          form={form}
          onChange={changeField}
          onSave={() => void save()}
          onCancel={cancelEdit}
          saving={saving}
          error={saveError}
        />
      ) : (
        <>
          <article className="mt-3 rounded-md border border-border-default bg-canvas p-3">
            <h2 className="text-[15px] font-semibold leading-snug text-text-primary">
              {view.title || 'Untitled draft'}
            </h2>
            <div className="mt-1 inline-flex items-center gap-1 text-[11px]">
              <span className="text-text-tertiary">Slug:</span>
              <code className="rounded-sm bg-inset px-1 py-0.5 font-mono text-[10px] text-text-secondary">
                {view.slug || '(none)'}
              </code>
            </div>

            <dl className="mt-3 space-y-1.5 border-t border-border-subtle pt-3">
              <SeoField label="Meta title" value={view.meta_title} />
              <SeoField label="Meta desc." value={view.meta_description} />
            </dl>

            {view.seo_target && view.seo_target.target_page ? (
              <div className="mt-3 rounded-md border border-ai/40 bg-ai-wash/40 p-2.5">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                  SEO target page
                </div>
                <p className="text-[13px] text-text-primary">
                  Links <span className="font-semibold">&ldquo;{view.seo_target.primary_keyword}&rdquo;</span>{' '}
                  to this page in the opening and closing paragraphs:
                </p>
                {safeHref(view.seo_target.target_page) ? (
                  <a
                    href={safeHref(view.seo_target.target_page) as string}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-0.5 block break-all text-[12px] font-medium text-ai underline"
                  >
                    {view.seo_target.target_page}
                  </a>
                ) : (
                  <span className="mt-0.5 block break-all text-[12px] text-text-secondary">
                    {view.seo_target.target_page}
                  </span>
                )}
                {typeof view.seo_target.position === 'number' ? (
                  <div className="mt-1 text-[11px] text-text-tertiary">
                    Current rank position {view.seo_target.position}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-3 border-t border-border-subtle pt-3">
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                Article
              </div>
              <MarkdownArticle md={view.body_md} />
            </div>

            {view.cta && view.cta.trim() !== '' && (
              <div className="mt-3 rounded-md border border-ai/30 bg-ai-wash/30 p-2.5">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                  Call to action
                </span>
                <p className="mt-0.5 text-[13px] font-medium text-text-primary">{view.cta}</p>
              </div>
            )}

            {seo.length > 0 && (
              <div className="mt-3 border-t border-border-subtle pt-3">
                <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                  SEO evidence
                </div>
                <ul className="space-y-1">
                  {seo.map((e, i) => (
                    <li key={i} className="text-[11px] leading-snug text-text-secondary">
                      <span className="font-semibold text-text-tertiary">{e.label} &middot; </span>
                      {e.url ? (
                        <a
                          href={safeHref(e.url) ?? undefined}
                          target="_blank"
                          rel="noreferrer"
                          className="text-ai underline decoration-ai/40 underline-offset-2"
                        >
                          {e.summary}
                        </a>
                      ) : (
                        e.summary
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </article>

          <ConnectorGatedNotice connectors={null} className="mt-3" />

          {/* Explicit, draft-only push of the APPROVED draft to a connected site. */}
          <WordPressPushControl draft={view} />

          {repurposeState !== 'idle' && (
            <div
              role="status"
              aria-live="polite"
              className="mt-3 rounded-md border border-border-default bg-canvas p-2.5"
            >
              {repurposeState === 'loading' && (
                <p className="text-[12px] text-text-secondary">
                  Preparing social drafts&hellip; Nothing is published or sent.
                </p>
              )}
              {repurposeState === 'error' && (
                <p role="alert" className="text-[12px] text-danger">
                  {repurposeError}
                </p>
              )}
              {repurposeState === 'done' &&
                repurposeResult &&
                (repurposeResult.available && repurposeResult.valid && repurposeResult.drafts.length > 0 ? (
                  <div>
                    <p className="mb-2 text-[11px] leading-snug text-text-secondary">
                      <span className="font-semibold text-text-tertiary">Draft social posts ready &middot; </span>
                      {repurposeResult.drafts.length} platform draft
                      {repurposeResult.drafts.length === 1 ? '' : 's'} prepared for your review in Content
                      Studio. Nothing was published, scheduled or sent.
                    </p>
                    <ul className="space-y-2">
                      {repurposeResult.drafts.map((d) => (
                        <li key={d.id} className="rounded-sm border border-border-subtle bg-inset p-2">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                              {d.platform}
                            </span>
                            <span className="font-mono text-[9px] text-text-tertiary">
                              {d.charCount}/{d.charLimit} chars &middot; {d.hashtagCount} tag
                              {d.hashtagCount === 1 ? '' : 's'}
                            </span>
                          </div>
                          <p className="mt-1 whitespace-pre-wrap text-[12px] text-text-primary">
                            {d.caption.length > 200 ? d.caption.slice(0, 200) + '\u2026' : d.caption}
                          </p>
                          {d.hashtags.length > 0 && (
                            <p className="mt-1 break-words text-[11px] text-ai">{d.hashtags.join(' ')}</p>
                          )}
                          {safeHref(d.editUrl) && (
                            <a
                              href={safeHref(d.editUrl) as string}
                              className="mt-1 inline-block text-[11px] font-medium text-ai underline"
                            >
                              Review in Content Studio
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="text-[12px] text-text-secondary">
                    {repurposeResult.message ||
                      'No social drafts could be prepared. Nothing was created, published or sent.'}
                  </p>
                ))}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {canEdit && (
              <button type="button" onClick={startEdit} className={primaryBtn}>
                Edit
              </button>
            )}
            <button type="button" onClick={() => void copy('text')} className={outlineBtn}>
              {copied === 'text' ? 'Copied ✓' : 'Copy text'}
            </button>
            <button type="button" onClick={() => void copy('html')} className={outlineBtn}>
              {copied === 'html' ? 'Copied ✓' : 'Copy HTML'}
            </button>
            <button
              type="button"
              onClick={() => void repurpose()}
              disabled={repurposeState === 'loading'}
              className={`${outlineBtn} disabled:opacity-60`}
            >
              {repurposeState === 'loading'
                ? 'Preparing…'
                : repurposeState === 'done' && repurposeResult?.valid
                  ? 'Repurposed ✓'
                  : 'Repurpose for social'}
            </button>
            <button type="button" onClick={onDismiss} className={ghostBtn}>
              Done
            </button>
          </div>
        </>
      )}
    </div>
  );
}
